import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import CountryTable from '../../../components/tables/CountryTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import {
  useCountrySearch,
  useDeleteCountry,
} from '../../../hooks/useCountryApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { CountryDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const CountryList: React.FC = () => {
  const [formFilters, setFormFilters] = useState<any>({}); // Form input values
  const [searchFilters, setSearchFilters] = useState<any>({}); // Actual search criteria
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<CountryDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { search } = useCountrySearch();
  const deleteCountry = useDeleteCountry();

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Countries', href: '#' },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await search({
        page: currentPage,
        pageSize,
        sortBy: 'Name',
        sortDescending: false,
        filters: searchFilters,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching Countries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [currentPage, pageSize, searchFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchFilters(formFilters);
  };

  const handleClearSearch = () => {
    setFormFilters({});
    setSearchFilters({});
    setCurrentPage(1);
  };

  const handleDelete = async (dto: CountryDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteCountry.mutateAsync(dto.id || '');
      await fetchItems();
    } catch (error) {
      console.error('Error deleting Country:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/countries"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.Country.View"
    >
      <Card
        title="Manage Countries"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/countries/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="name"
              label="Search by Name"
              value={formFilters.name || ''}
              onChange={(e) =>
                setFormFilters({ ...formFilters, name: e.target.value })
              }
              placeholder="Search by Name"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <CountryTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/countries/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default CountryList;
